import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AvatarChip } from "components/designV2/avatarChip/avatarChip";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { Modal } from "components/designV2/modal/modal";
import { StatusBanner } from "components/designV2/statusBanner/statusBanner";
import { TooltipHover } from "components/designV2/tooltip/tooltip";
import { ColorV2 } from "config/colors";
import { GigType, PaymentMethod } from "model/Gig";
import { ScheduleStatus } from "model/Schedule";
import { ApplicationResolution, ApplicationStatus } from "model/Application";
import {
  getBlockStatusStyling,
  getBlockStatusText,
  getShiftDetailTexts,
} from "web-apps/company/components/schedule/utils/schedule.status";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import type {
  TeamPublicDetailsEditActions,
  TeamPublicDetailsEditViewState,
  TeamPublicDetailsModalProps,
} from "./teamPublicDetailsModal.contract";
import { resolveApplicationActionModalCopy } from "./teamPublicDetailsModal.applicationAction.logic";
import { TeamPublicDetailsModalApplicationsSection } from "./teamPublicDetailsModal.applicationsSection.component";
import { TeamPublicDetailsModalEmployeesSection } from "./teamPublicDetailsModal.employeesSection.component";
import { TeamPublicDetailsModalGigSection } from "./teamPublicDetailsModal.gigSection.component";
import { TeamPublicDetailsModalDialogs } from "./teamPublicDetailsModal.dialogs.component";
import { useTeamPublicDetailsEditState } from "./useTeamPublicDetailsEditState.logic";
import { useTeamPublicDetailsActions } from "./useTeamPublicDetailsActions.logic";
import { useTeamPublicDetailsData } from "./useTeamPublicDetailsData.logic";
import { ModalContent } from "../shiftDetailsModal.styled";

export const TeamPublicDetailsModal: React.FC<TeamPublicDetailsModalProps> = (
  props,
) => {
  const { t, i18n } = useTranslation();
  const [companyState] = useCompanyStore();
  const [isMotivationalLetterModalOpen, setIsMotivationalLetterModalOpen] =
    useState(false);
  const [selectedMotivationalLetter, setSelectedMotivationalLetter] =
    useState<string>("");
  const dataState = useTeamPublicDetailsData({
    isOpen: props.isOpen,
    companyId: companyState.company?.id,
    gigId: props.selectedShift.gigId,
  });

  const statusStyling = getBlockStatusStyling(props.status);
  const statusLabel = getBlockStatusText(props.status, t);
  const statusDetails = getShiftDetailTexts(props.status, t);
  const statusSubLabel = useMemo(() => {
    if (props.status === ScheduleStatus.OPEN) {
      return t("Schedule.TeamPublicNotfilledStatusText");
    }
    return statusDetails.text;
  }, [props.status, statusDetails.text, t]);
  const isPublicShift =
    (dataState.gigData?.type ?? props.selectedShift.gigType) === GigType.PUBLIC;
  const avatarUrls = useMemo(
    () => ({
      ...companyState.companyWorkerImages,
      ...dataState.avatarUrls,
    }),
    [companyState.companyWorkerImages, dataState.avatarUrls],
  );
  const workerTitle =
    props.worker.isWorker === false
      ? t(`Schedule.${props.worker.heading}`, {
          defaultValue: props.worker.heading,
        })
      : props.worker.heading;

  const editState = useTeamPublicDetailsEditState({
    companyId: companyState.company?.id,
    gigData: dataState.gigData,
    setGigData: dataState.setGigData,
    t,
  });

  const actionsState = useTeamPublicDetailsActions({
    companyId: companyState.company?.id,
    setGigData: dataState.setGigData,
  });

  const paymentTypeLabel = useMemo((): string => {
    if (!dataState.gigData?.paymentType) return "-";
    if (dataState.gigData.paymentType === PaymentMethod.NONE) {
      return t("System.OwnSalaryManagement");
    }
    return t(`System.${dataState.gigData.paymentType}`, {
      defaultValue: dataState.gigData.paymentType,
    });
  }, [dataState.gigData?.paymentType, t]);

  const skillsLabel = useMemo((): string => {
    const skills =
      dataState.gigData?.skills
        ?.map((skillItem) => skillItem.skill?.trim())
        .filter((skill): skill is string => Boolean(skill)) ?? [];
    if (skills.length === 0) return "-";
    return skills.join(", ");
  }, [dataState.gigData?.skills]);

  const { applications, hiredApplications } = useMemo(() => {
    const applications = dataState.gigData?.applications ?? [];
    const applicationList: typeof applications = [];
    const hiredApplications: typeof applications = [];

    applications.forEach((application) => {
      if (
        application.status === ApplicationStatus.CLOSED &&
        application.resolution === ApplicationResolution.ACCEPTED
      ) {
        hiredApplications.push(application);
      } else {
        applicationList.push(application);
      }
    });

    return { applications: applicationList, hiredApplications };
  }, [dataState.gigData?.applications]);

  const editViewState: TeamPublicDetailsEditViewState = {
    isEditing: editState.isEditing,
    isSaving: editState.isSaving,
    saveError: editState.saveError,
    editRole: editState.editRole,
    editAmountOfWorkers: editState.editAmountOfWorkers,
    editDescription: editState.editDescription,
    editSkillInput: editState.editSkillInput,
    editSkills: editState.editSkills,
    editPaymentType: editState.editPaymentType,
    editMinRate: editState.editMinRate,
    editMaxRate: editState.editMaxRate,
  };

  const editActions: TeamPublicDetailsEditActions = {
    setEditRole: editState.setEditRole,
    setEditAmountOfWorkers: editState.setEditAmountOfWorkers,
    setEditDescription: editState.setEditDescription,
    setEditSkillInput: editState.setEditSkillInput,
    setEditPaymentType: editState.setEditPaymentType,
    setEditMinRate: editState.setEditMinRate,
    setEditMaxRate: editState.setEditMaxRate,
    handleAddSkill: editState.handleAddSkill,
    handleRemoveSkill: editState.handleRemoveSkill,
    handleCancelEdit: editState.handleCancelEdit,
    handleSaveEdit: async (): Promise<void> => {
      if (props.readOnly) return;
      await editState.handleSaveEdit();
    },
  };

  const applicationActionModalCopy = useMemo(() => {
    if (actionsState.applicationModalState.type !== "confirm") return null;
    return resolveApplicationActionModalCopy({
      action: actionsState.applicationModalState.action,
      language: i18n.language,
      t,
    });
  }, [actionsState.applicationModalState, i18n.language, t]);

  const handleOpenMotivationalLetterModal = (
    motivationalLetter: string,
  ): void => {
    setSelectedMotivationalLetter(motivationalLetter);
    setIsMotivationalLetterModalOpen(true);
  };

  const handleCloseMotivationalLetterModal = (): void => {
    setIsMotivationalLetterModalOpen(false);
    setSelectedMotivationalLetter("");
  };

  const editButton = (
    <SecondaryButton
      label={t("Schedule.TeamPublicEditShift")}
      onClick={() => {
        if (!props.readOnly) editState.handleStartEdit();
      }}
      disabled={props.readOnly}
    />
  );
  const footerContent = !editState.isEditing ? (
    props.readOnly ? (
      <TooltipHover text={t("Subscription.UpgradeToAccess")}>
        {editButton}
      </TooltipHover>
    ) : (
      editButton
    )
  ) : null;

  return (
    <>
      <Modal
        label={t("Schedule.TeamPublicDetailsTitle")}
        isOpen={props.isOpen}
        onClose={props.onClose}
        size="medium"
        footer={footerContent}
      >
        <ModalContent>
          <AvatarChip
            title={workerTitle}
            subTitle={props.worker.text ?? null}
            iconName="v2/team"
            avatarBackgroundColor={ColorV2.Blue6}
          />

          <StatusBanner
            iconName="v2/plus"
            label={statusLabel}
            subLabel={statusSubLabel}
            textColor={statusStyling.color}
            backgroundColor={statusStyling.backgroundColor}
            fullWidth
          />

          <TeamPublicDetailsModalGigSection
            selectedShiftGigType={props.selectedShift.gigType}
            isLoading={dataState.isLoading}
            isError={dataState.isError}
            gigData={dataState.gigData}
            paymentTypeLabel={paymentTypeLabel}
            skillsLabel={skillsLabel}
            editViewState={editViewState}
            editActions={editActions}
          />
          <TeamPublicDetailsModalApplicationsSection
            applications={applications}
            activeApplicationId={actionsState.activeApplicationId}
            avatarUrls={avatarUrls}
            isPublicShift={isPublicShift}
            canManageApplications={!props.readOnly}
            onOpenMotivationalLetterModal={handleOpenMotivationalLetterModal}
            onOpenApplicationActionConfirm={
              actionsState.handleOpenApplicationActionConfirm
            }
          />
          <TeamPublicDetailsModalEmployeesSection
            hiredApplications={hiredApplications}
            amountOfWorkers={dataState.gigData?.amountOfWorkers ?? 0}
            avatarUrls={avatarUrls}
            isPublicShift={isPublicShift}
            onOpenMotivationalLetterModal={handleOpenMotivationalLetterModal}
          />
        </ModalContent>
      </Modal>
      <TeamPublicDetailsModalDialogs
        isMotivationalLetterModalOpen={isMotivationalLetterModalOpen}
        selectedMotivationalLetter={selectedMotivationalLetter}
        onCloseMotivationalLetterModal={handleCloseMotivationalLetterModal}
        applicationModalState={
          props.readOnly
            ? { type: "closed" }
            : actionsState.applicationModalState
        }
        applicationActionModalCopy={applicationActionModalCopy}
        isApplicationActionLoading={actionsState.isApplicationActionLoading}
        onCloseApplicationActionModal={actionsState.handleCloseApplicationModal}
        onConfirmApplicationAction={async (declineReason): Promise<void> => {
          if (props.readOnly) return;
          await actionsState.handleConfirmApplicationAction(declineReason);
        }}
        onCloseApplicationErrorModal={actionsState.handleCloseApplicationModal}
      />
    </>
  );
};
